/**
 * Loom onboarding step manager.
 *
 * Single source of truth for "which screens does this user see, in what
 * order?". Everything variant- or user-specific about the flow lives here.
 *
 * ─── How to edit ────────────────────────────────────────────────────────
 *  - Add / reorder screens in a variant:  edit `STEPS_BY_VARIANT`.
 *  - Branch on user data (persona, cloud
 *    state, feature flags, etc.):         extend `useStepsForUser` — that's
 *                                         the only place path resolution
 *                                         happens.
 *  - Add a new step surface entirely:     add the constant and component
 *                                         map in `./types.ts`, then
 *                                         reference the constant from any
 *                                         variant list below.
 *
 * The view (`loom-onboarding-view.tsx`) knows nothing about which steps
 * exist, what order they run in, or which experiment gates them — it just
 * renders `<StepManager />`.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { ufoEventLoomOnboardingFlowDuration } from '@/components/signup/utils/ufo-events';

import { CornerLoomLogo } from '../components/corner-loom-logo';
import { GradientBackground } from '../components/gradient-background';

import { useLoomOnboardingExperiment, LoomOnboardingVariants } from './is-exp-loom-onboarding';
import { useLoomOnboardingAnalytics } from './loom-onboarding-analytics';
import {
	onboardingSource,
	ONBOARDING_STEP_COMPONENTS,
	type DefaultStepAnalyticsAttributes,
	type OnboardingStep,
	StepCompletionPayload,
} from './types';

/**
 * Ordered list of steps a user sees, per experiment variant. Order in each
 * array IS the flow order.
 *
 * `CONTROL` intentionally has no steps — a control user shouldn't reach the
 * onboarding view at all (see `product-view-switch.tsx`); the empty list
 * makes the fallback render nothing instead of crashing if it does.
 */
const STEPS_BY_VARIANT: Record<LoomOnboardingVariants, readonly OnboardingStep[]> = {
	[LoomOnboardingVariants.VARIANT_1]: ['persona-use-case-step', 'download-options-step'],
	[LoomOnboardingVariants.CONTROL]: [],
};

/**
 * Resolves the ordered list of steps for the current user.
 *
 * Right now this only branches on the experiment variant. To also gate on
 * user data extend this one hook.
 */
const useStepsForUser = (): readonly OnboardingStep[] => {
	const { loomOnboardingToWacVariant } = useLoomOnboardingExperiment();
	return useMemo(
		() => (loomOnboardingToWacVariant ? STEPS_BY_VARIANT[loomOnboardingToWacVariant] : []),
		[loomOnboardingToWacVariant],
	);
};

type StepManagerProps = {
	/**
	 * Called when the user continues from the last step. The step manager is
	 * agnostic to what this does — the onboardingview owns cloud-status / redirect
	 * logic.
	 */
	readonly handleContinue: () => void;
};

/**
 * Renders the progress bar and the component for whichever step the user is
 * currently on.
 * Owns the "where am I in the flow?" state; the view above
 * owns everything else (errors, redirects, cloud status, telemetry).
 */
export const StepManager = ({ handleContinue }: StepManagerProps) => {
	const steps = useStepsForUser();
	const [index, setIndex] = useState(0);
	const { fireStepViewed, fireStepCompleted } = useLoomOnboardingAnalytics();

	// `steps.length === 0` is guarded below on the initial render, but this
	// hook still needs to run unconditionally per React rules — the guard
	// inside the effect handles the empty-steps case.
	const currentStep = steps[index] ?? steps[0];

	const analyticsAttributes = useMemo<DefaultStepAnalyticsAttributes>(
		() => ({
			flowId: 'default',
			source: onboardingSource,
			step: currentStep as OnboardingStep,
			stepIndex: index,
		}),
		[currentStep, index],
	);

	// Fire `onboardingStep viewed` every time the visible step changes.
	// On the first step, also start the "onboarding flow duration" UFO
	// timer — stopped when `AfterOnboardingStep` mounts.
	useEffect(() => {
		if (!currentStep) return;
		if (index === 0) {
			void ufoEventLoomOnboardingFlowDuration.start();
		}
		fireStepViewed(analyticsAttributes, index === 0);
	}, [index, fireStepViewed, analyticsAttributes]);

	const onContinue = useCallback(
		(payload?: StepCompletionPayload) => {
			const completedStep = steps[index];
			if (completedStep) {
				fireStepCompleted(analyticsAttributes, payload?.action, payload?.persona);
			}

			if (index >= steps.length - 1) {
				// Last step — the flow is done. `onboardingFlow completed` is
				// fired from `AfterOnboardingStep`, where the provisioning state
				// is available to derive `hasProvisioningTimeLeft`.
				handleContinue();
				return;
			}
			setIndex((current) => current + 1);
		},
		[fireStepCompleted, handleContinue, index, steps, analyticsAttributes],
	);

	const canGoBack = index > 0;
	const onBack = useCallback(() => {
		setIndex((current) => (current > 0 ? current - 1 : current));
	}, []);

	if (steps.length === 0) {
		// Defensive: `product-view-switch` should never route a stepless
		// variant here, but if it does we'd rather render nothing than an
		// empty shell with a broken progress bar.
		return null;
	}

	const StepComponent = ONBOARDING_STEP_COMPONENTS[currentStep as OnboardingStep];

	return (
		<Fragment>
			<CornerLoomLogo includeBackButton={canGoBack} onBack={onBack} />
			<GradientBackground>
				<StepComponent onContinue={onContinue} />
			</GradientBackground>
		</Fragment>
	);
};
